/**
 * Scroll animations — auto-targeting (approach B).
 *
 * Uses the standard GSAP pattern: scrollTrigger option directly in the tween.
 * Subtle: 24 px rise, 0.7 s, fires once, no reverse.
 * Bails out entirely if prefers-reduced-motion is set.
 *
 * Targets Laura Sheppard's actual template markup (front-page.php,
 * page-about.php, page-programs.php) — see each page's hero shape in
 * initIntroAnimation() and the scroll-triggered sections in
 * initScrollAnimations().
 */

import { gsap, ScrollTrigger } from '../gsap/init';

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const FROM_Y = 24;
const DURATION = 0.7;
const EASE = 'power2.out';
const TRIGGER_START = 'top 88%';

// ─── Entry point ──────────────────────────────────────────────────────────────

export function initAnimations(): void {
  if (typeof gsap === 'undefined' || !ScrollTrigger) {
    console.warn('GSAP or ScrollTrigger not available');
    return;
  }

  if (REDUCED_MOTION) return;

  initIntroAnimation();
  initScrollAnimations();
  initKickUnderline();
}

// ─── Above-fold intro — fires on page load ────────────────────────────────────
//
// Each template's hero has a different shape, so each is matched by its own
// wrapper class rather than a shared generic selector:
//   - Home (.hero-text): kicker + h1 + lead + cta-row + trust chips
//   - About (.abt-hero > div): kicker + h1 + lead (no cta-row/trust)
//   - Programs (.phead > div): kicker + h1 + lead (no cta-row/trust)
//   - Story (.phead > div): kicker + KolKid logo + h1 + lead

function initIntroAnimation(): void {
  const heroEl = document.querySelector<HTMLElement>('.hero-text, .abt-hero > div, .phead > div');

  if (heroEl) {
    const els = Array.from(
      heroEl.querySelectorAll<HTMLElement>('.kick, h1, .lead, .cta-row, .trust, .kolkid-logo')
    );

    // About page's photo is a sibling of the text wrapper (both direct
    // children of .abt-hero), not a descendant of heroEl — appended here so
    // it fades in with the rest of the above-fold intro instead of not
    // animating at all.
    const aboutPhoto = document.querySelector<HTMLElement>('.abt-photo');
    if (aboutPhoto) els.push(aboutPhoto);

    if (els.length) {
      gsap.fromTo(
        els,
        { opacity: 0, y: FROM_Y },
        {
          opacity: 1,
          y: 0,
          duration: DURATION,
          ease: EASE,
          stagger: 0.12,
          delay: 0.1,
          clearProps: 'transform',
        }
      );
    }
  }

  // Home hero's + Programs header's cmyk-circles clusters build in alongside
  // the text, one circle after another, on load (not scroll-triggered —
  // both are above the fold like the rest of the intro).
  fadeInCircles('.hero > .cmyk-circles', 0.15, false);
  fadeInCircles('.phead > .cmyk-circles', 0.15, false);
}

/**
 * Builds in a cmyk-circles cluster's individual <circle> elements, staggered,
 * settling at their resting 0.75 opacity rather than 1 — full opacity would
 * lose the translucent overlap look the circles rely on.
 */
function fadeInCircles(clusterSelector: string, stagger: number, scrollTriggered: boolean): void {
  const circles = Array.from(document.querySelectorAll<HTMLElement>(`${clusterSelector} circle`));
  if (!circles.length) return;

  gsap.fromTo(
    circles,
    { opacity: 0, y: FROM_Y },
    {
      opacity: 0.75,
      y: 0,
      duration: DURATION,
      ease: EASE,
      stagger,
      clearProps: 'transform',
      ...(scrollTriggered
        ? { scrollTrigger: { trigger: circles[0], start: TRIGGER_START, once: true } }
        : { delay: 0.1 }),
    }
  );
}

// ─── Kick underline — draws in left to right ──────────────────────────────────
//
// Applies to every .kick site-wide — hero kickers (Home/About/Programs) and
// section eyebrows alike. The actual transform tween lives in CSS (.kick's
// ::after, see _hero.scss), since GSAP can't target a ::after directly — this
// just flips a class via ScrollTrigger. One mechanism covers both above- and
// below-the-fold kicks: ScrollTrigger fires onEnter immediately for a
// trigger that's already past its start line at creation time, so hero
// kickers (in view on load) draw in right away, while below-the-fold ones
// (section eyebrows, the about-teaser kicker) wait for the real scroll.
function initKickUnderline(): void {
  document.querySelectorAll<HTMLElement>('.kick').forEach(el => {
    ScrollTrigger.create({
      trigger: el,
      start: TRIGGER_START,
      once: true,
      onEnter: () => el.classList.add('kick-in'),
    });
  });
}

// ─── Scroll-triggered ─────────────────────────────────────────────────────────

function initScrollAnimations(): void {
  // Section headings — the shared .sec-head kicker+h2 pattern used by every
  // "three things" / "how it works" / "programs" / "my story" / "beliefs" /
  // "faq" section header across all three templates. Scoped to .sec-head
  // (rather than a bare `section h2`) so it doesn't double-fire on the
  // .about-text and .noteband headings, which are animated as part of their
  // own grouped reveals below.
  document.querySelectorAll<HTMLElement>('.sec-head h2').forEach(el => fadeUp(el));

  // Home — "three things, guaranteed" + About — "four things i stand by"
  // Both render .block items (icon-block.php partial), staggered.
  staggerGroup('.blocks .block, .beliefs .block', 0.12);

  // Home — "how it works" steps, staggered
  staggerGroup('.steps .step', 0.12);

  // Home — program list rows (.plist .prow), staggered
  staggerGroup('.plist .prow', 0.08);

  // Programs page — program cards (.progs .prog2), staggered
  staggerGroup('.progs .prog2', 0.08);

  // Home — about teaser copy (kicker + heading + body + link)
  staggerChildren('.about-text', '.kick, h2, p, .go', 0.1);

  // Home — about teaser's small cmyk-circles cluster, builds in alongside
  // the teaser copy when the section scrolls into view.
  fadeInCircles('.wrap.about > .cmyk-circles', 0.15, true);

  // Home's Proof section, and Story's "an example" + closing sections — all
  // three render a `.proof-story` text block (copy, sometimes a blockquote,
  // sometimes a "read the whole story" link); staggerChildren() now handles
  // Story's two instances the same as Home's one (see its own doc comment).
  staggerChildren('.proof-story', 'p, blockquote, .go', 0.1);
  // The `.stat-box` boxed reveal only ever appears once per page (Home's
  // Proof section or Story's "an example" section, never both).
  const statBox = document.querySelector<HTMLElement>('.proof .stat-box');
  if (statBox) fadeUp(statBox);

  // Story page — "the client" section's browser-chrome screenshot mock
  const siteShot = document.querySelector<HTMLElement>('.site-shot');
  if (siteShot) fadeUp(siteShot);

  // Story page — "the method" section's 6-card grades scorecard, staggered
  staggerGroup('.grades .grade', 0.08);

  // About page — "my story" prose
  const prose = document.querySelector<HTMLElement>('.prose');
  if (prose) fadeUp(prose);

  // Programs page — "not sure which is right?" band (heading + body + button)
  staggerChildren('.noteband', 'h2, p, .btn-fill', 0.12);

  // Programs page — FAQ items, staggered
  staggerGroup('.faq .qa', 0.1);

  // CTA band — heading + body + button, paired (Home, About, Programs all
  // share the same .cta markup shape)
  staggerChildren('.cta', 'h2, p, .btn-fill', 0.15);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Fade up a group of matching elements together, staggered, triggered off
 * the first element in the group. No-ops if the selector matches nothing.
 */
function staggerGroup(selector: string, stagger: number): void {
  const els = Array.from(document.querySelectorAll<HTMLElement>(selector));
  if (!els.length) return;

  gsap.fromTo(
    els,
    { opacity: 0, y: FROM_Y },
    {
      opacity: 1,
      y: 0,
      duration: DURATION,
      ease: EASE,
      stagger,
      clearProps: 'transform',
      scrollTrigger: { trigger: els[0], start: TRIGGER_START, once: true },
    }
  );
}

/**
 * Fade up each matching wrapper's own direct-content children together,
 * staggered, triggered off that wrapper. Used for heading+body+button bands
 * (.about-text, .noteband, .cta, .proof-story) where the wrapper isn't
 * itself a fade target — only the pieces inside it are. Applies to every
 * element matching wrapperSelector, not just the first — most call sites
 * only ever render one per page, but the Story template renders two
 * `.proof-story` blocks ("an example" + the closing section), so this can't
 * assume a single match. No-ops per wrapper with none of its matching
 * children present.
 */
function staggerChildren(wrapperSelector: string, childSelector: string, stagger: number): void {
  document.querySelectorAll<HTMLElement>(wrapperSelector).forEach(wrapper => {
    const els = Array.from(wrapper.querySelectorAll<HTMLElement>(childSelector));
    if (!els.length) return;

    gsap.fromTo(
      els,
      { opacity: 0, y: FROM_Y },
      {
        opacity: 1,
        y: 0,
        duration: DURATION,
        ease: EASE,
        stagger,
        clearProps: 'transform',
        scrollTrigger: { trigger: wrapper, start: TRIGGER_START, once: true },
      }
    );
  });
}

function fadeUp(el: HTMLElement): void {
  gsap.fromTo(
    el,
    { opacity: 0, y: FROM_Y },
    {
      opacity: 1,
      y: 0,
      duration: DURATION,
      ease: EASE,
      clearProps: 'transform',
      scrollTrigger: { trigger: el, start: TRIGGER_START, once: true },
    }
  );
}
